import { useState, useEffect, useRef } from "react";
import { useQueryClient, useMutation } from '@tanstack/react-query';
import authAxios from "../../components/authAxios";

import { TextField } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { Modal, Radio, message } from 'antd';

import moment from 'moment';
import NumberFormat from "react-number-format";
import UserAlerts from "../../components/UserAlerts";
import './Rentals.css'


function PropertyEdit({property, clientType, isModalOpen, setIsModalOpen}) {
    const [error, setError] = useState('');
    const errorRef = useRef(null);
    const queryClient = useQueryClient();
    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [managementFee, setManagementFee] = useState('');
    const [effectiveDate, setEffectiveDate] = useState(null);

    const [nameError, setNameError] = useState(false)
    const [addressError, setAddressError] = useState(false);
    const [cityError, setCityError] = useState(false);
    const [effectiveDateError, setEffectiveDateError] = useState(false);

    const [changeType, setChangeType] = useState('10');

    const contactRef = useRef("");
    contactRef.showContactType=true    

    const mutation = useMutation({
        mutationFn: (propertyData) => {
            return authAxios.put(`rentals/properties/${property.id}/`, propertyData);
        },
        onError: (error, variables, context) => {
            if (error.response.data.error){
                // If it's an array, display each error message
                if (Array.isArray(error.response.data.error)) {
                    error.response.data.error.map((errorMessage) => (
                        setError(errorMessage)
                    ));
                } else {
                    // Handle single error message
                    setError(error.response.data.error); 
                }
            } else {
                // Fallback to Standard error message
                setError(error.message); 
            }      
        },
        onSuccess: (result) => {
            message.success("Property was amended successfully");
            queryClient.invalidateQueries(['properties', 'property']);
            queryClient.refetchQueries(['properties']);
            queryClient.refetchQueries(['property']);
            setIsModalOpen(false)
        },
    })

    const mutationManagementFee = useMutation({
        mutationFn: (feeData) => {
            return authAxios.put(`rentals/properties/${property.id}/change-management-fee/`, feeData);
        },
        onError: (error, variables, context) => {
            if (error.response.data.error){
                // If it's an array, display each error message
                if (Array.isArray(error.response.data.error)) {
                    error.response.data.error.map((errorMessage) => (
                        setError(errorMessage)
                    ));
                } else {
                    // Handle single error message
                    setError(error.response.data.error); 
                }
            } else {
                // Fallback to Standard error message
                setError(error.message); 
            }      
        },
        onSuccess: (result) => {
            message.success("Management fee revision was successful");
            queryClient.invalidateQueries(['properties', 'property']);
            queryClient.refetchQueries(['properties']);
            queryClient.refetchQueries(['property']);
            setIsModalOpen(false)
        },
    })


    const handleSubmit = (e) => {
        e.preventDefault();

        if (changeType === "10") {
            
            if (name === '') {
                setNameError(true)
            }

            if (address === '' ) {
                setAddressError(true)
            }

            if (city === '' ) {
                setCityError(true)
            }

            if (name && address && city ) {
                const propertyData = {name, address, city };
                console.log(propertyData)
                mutation.mutate(propertyData)
            } 
        }

        if (changeType === "20") {
            if (!effectiveDate) {
                setEffectiveDateError(true)
                return;
            }

            const fee_structure = property.management_contract.fee_structure? property.management_contract.fee_structure : null;
            const fee_percentage = managementFee? parseFloat(managementFee) / 100 : null;
            const effective_date = moment(effectiveDate).format("YYYY-MM-DD");
            const feeData = {fee_structure, fee_percentage, effective_date    };
            console.log(feeData)
            mutationManagementFee.mutate(feeData)
        }
    }

    useEffect(()=>{   
        if (property){
            setName(property.name);
            setAddress(property.address)
            setCity(property.city)
            setManagementFee(parseFloat(property.management_fee) * 100)
        }
    },[property])

    useEffect(()=>{
        if (error){
            errorRef.current.scrollIntoView();  
        }            
    },[error])

    return(
        <>
        {isModalOpen && 
            <Modal 
                className="dashboard-modal" 
                title="Edit Property" 
                visible={isModalOpen} 
                open={isModalOpen} 
                onOk={handleSubmit} 
                onCancel={handleCancel}
                confirmLoading={mutation.isLoading || mutationManagementFee.isLoading}
            >
                <div className="dashboard-form">

                    <div ref={errorRef}>
                        <UserAlerts error={error}  />
                    </div>

                    <form noValidate autoComplete="off" onSubmit={handleSubmit}>

                        {clientType === "010" && 
                        <Radio.Group 
                            className="custom-input" 
                            onChange={(e) => setChangeType(e.target.value)}
                            value={changeType}
                        >
                            <Radio.Button value="10">Profile</Radio.Button>
                            <Radio.Button value="20">Management Fee</Radio.Button>
                        </Radio.Group>
                        }

                        {changeType === "10" &&
                        <>

                        <TextField className="custom-input"
                            value={name}
                            onChange={(e) => {
                                setNameError(false)
                                setName(e.target.value)
                            }}
                            label="Name" 
                            variant="outlined" 
                            fullWidth
                            required
                            error={nameError}
                        />

                        <TextField className="custom-input"
                            value={address}
                            onChange={(e) => {
                                setAddressError(false)
                                setAddress(e.target.value)
                            }}
                            label="Physical Address" 
                            variant="outlined" 
                            fullWidth
                            required
                            error={addressError}
                        />  

                        <TextField className="custom-input"
                            value={city}
                            onChange={(e) => {
                                setCityError(false)
                                setCity(e.target.value)
                            }}
                            label="City/Town" 
                            variant="outlined" 
                            fullWidth
                            required
                            error={cityError}
                        />  
                        </>
                        }

                        {changeType === "20" && 
                        <ManagementFeeEdit 
                            property={property} 
                            managementFee={managementFee}
                            setManagementFee={setManagementFee}
                            effectiveDate={effectiveDate}
                            setEffectiveDate={setEffectiveDate}
                            effectiveDateError={effectiveDateError}
                            setEffectiveDateError={setEffectiveDateError}
                        />
                        }

                    </form>
                </div>
            </Modal>
        }
        </>

    )

}

export default PropertyEdit;


const ManagementFeeEdit = ({
    property, 
    managementFee, 
    setManagementFee, 
    effectiveDate, 
    setEffectiveDate,
    effectiveDateError,
    setEffectiveDateError
}) => {
    return (
        <>
        <TextField className="custom-input"
            value={property.name || ''}
            label="Name" 
            variant="outlined" 
            fullWidth
            disabled
        />

        <TextField className="custom-input"
            value={property.owner || ''}
            label="Owner" 
            variant="outlined" 
            fullWidth
            disabled
        />

        <Radio.Group 
            className="custom-input" 
            value={property.management_contract.fee_structure}
            readOnly
        >
            <Radio value="010">Flat</Radio>
            <Radio value="020">Percentage</Radio>
        </Radio.Group>

        <NumberFormat 
            value = {managementFee}
            onChange={(e) => {
                setManagementFee(e.target.value)
            }}
            className="custom-input"
            fullWidth
            label="Management Fee" 
            variant="outlined" 
            customInput={TextField}
            suffix="%"
            decimalScale={2}
            fixedDecimalScale
            decimalSeparator="."
            allowNegative={false}
            isNumericString
        />

        <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
                views={['year', 'month']}
                value={effectiveDate}
                label="Effective Date"
                // inputFormat="DD-MM-YYYY" 
                inputFormat="MMM YYYY"
                onChange={(newDate) => {
                    // setEffectiveDate(moment(newDate).format("YYYY-MM-DD"));
                    const selectedDate = moment(newDate).date(1).format("YYYY-MM-DD");
                    setEffectiveDate(selectedDate)
                    setEffectiveDateError(false)
                }}
                renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      className="custom-input"
                      error={effectiveDateError}
                    />
                  )}
            />
        </LocalizationProvider>
    </>
    )
}

